/**
 * `/answer/documents` — Knowledge-base upload + list (M-ANSWER.3).
 *
 * Mirrors `/quote/documents`: drag-in a PDF/DOCX/TXT, watch the status
 * badge transition pending → processing → ready (live-polled via the
 * `useAnswerDocuments` hook's refetchInterval). Click a row to drill
 * into detail when that surface lands (M-ANSWER.5 polish).
 */
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useAnswerDocuments,
  useDeleteAnswerDocument,
  useUploadAnswerDocument,
} from "@/hooks/use-answer";
import { formatTimestamp } from "@/lib/formatters";

export function AnswerDocumentsPage() {
  const list = useAnswerDocuments();
  const uploadMut = useUploadAnswerDocument();
  const deleteMut = useDeleteAnswerDocument();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState("");

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const file = fileInputRef.current?.files?.[0];
    if (!file) return;
    uploadMut.mutate(
      { file, ...(title.trim() ? { title: title.trim() } : {}) },
      {
        onSuccess: () => {
          if (fileInputRef.current) fileInputRef.current.value = "";
          setTitle("");
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Answer</p>
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">Knowledge base</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Upload security policies, RFP responses, and reference docs. The AI grounds
          questionnaire drafts in this content with verifiable citations.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Upload document</CardTitle>
          <CardDescription>PDF, DOCX, TXT, or EML. Up to 25 MB.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="flex flex-wrap items-center gap-3">
            <input
              type="file"
              ref={fileInputRef}
              accept=".pdf,.docx,.txt,.eml,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,message/rfc822"
              className="text-sm"
              required
            />
            <input
              className="min-w-[18rem] flex-1 rounded border border-input bg-background px-3 py-2 text-sm"
              placeholder="Title (optional — defaults to file name)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <Button type="submit" disabled={uploadMut.isPending}>
              {uploadMut.isPending ? "Uploading…" : "Upload"}
            </Button>
            {uploadMut.error && (
              <span className="text-sm text-destructive">{uploadMut.error.message}</span>
            )}
          </form>
        </CardContent>
      </Card>

      {list.isLoading && <Skeleton className="h-16 w-full" />}
      {list.error && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">
              Couldn't load documents: {list.error.message}
            </p>
          </CardContent>
        </Card>
      )}
      {list.data && list.data.items.length === 0 && !list.isLoading && (
        <Card>
          <CardHeader>
            <CardTitle>No documents uploaded</CardTitle>
            <CardDescription>
              Upload a few policy or reference documents so the AI has context for drafting
              answers.
            </CardDescription>
          </CardHeader>
        </Card>
      )}
      {list.data && list.data.items.length > 0 && (
        <ul className="grid grid-cols-1 gap-2" data-testid="answer-docs-list">
          {list.data.items.map((doc) => (
            <li key={doc.id}>
              <Card>
                <CardContent className="flex items-start justify-between gap-3 pt-6">
                  <Link
                    to={`/answer/documents/${doc.id}`}
                    className="min-w-0 flex-1 rounded outline-none hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-primary/40"
                    data-testid={`answer-doc-row-link-${doc.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{doc.title ?? doc.file_name}</span>
                      <Badge variant={doc.status === "ready" ? "default" : "outline"}>
                        {doc.status}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {doc.file_size_bytes != null
                        ? `${Math.round(doc.file_size_bytes / 1024)} KB · `
                        : ""}
                      {doc.page_count > 0 ? `${doc.page_count} pages · ` : ""}
                      {formatTimestamp(doc.created_at)}
                    </div>
                    {doc.error_message && (
                      <div className="text-xs text-destructive">{doc.error_message}</div>
                    )}
                  </Link>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteMut.mutate(doc.id)}
                    disabled={deleteMut.isPending}
                  >
                    Delete
                  </Button>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
